import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldAlert, Building2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function DriverPayout() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ payout_iban: "", payout_account_holder: "", payout_bank_name: "", payout_currency: "CHF" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/driver/payout");
      setData(data);
      setForm({
        payout_iban: data.iban || "",
        payout_account_holder: data.account_holder || "",
        payout_bank_name: data.bank_name || "",
        payout_currency: data.currency || "CHF",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/driver/payout", form);
      toast.success("Dati pagamento salvati. In attesa di verifica admin.");
      setEditing(false);
      await load();
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;

  const status = data.status || "missing";
  const hasIban = !!data.iban;

  return (
    <div className="space-y-5" data-testid="driver-payout-page">
      <Link to="/driver/profile" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Profilo
      </Link>
      <div>
        <p className="overline">Pagamenti</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">Coordinate bancarie</h1>
        <p className="text-dapnow-muted text-sm mt-2">La remunerazione dei driver interni è gestita da DapNow fuori da Stripe. Compila questi dati solo se richiesti dal tuo referente; non sono necessari per accettare consegne.</p>
      </div>

      <div className={`card-soft p-5 flex items-center gap-3 ${status === "verified" ? "border-emerald-200 bg-emerald-50" : status === "pending" ? "border-amber-200 bg-amber-50" : "border-dapnow-line"}`}>
        {status === "verified" ? (
          <><ShieldCheck className="h-5 w-5 text-emerald-700" /><div><p className="font-semibold tracking-tight">IBAN verificato</p><p className="text-xs text-dapnow-muted">Coordinate verificate dal team. Modalità e tempi di remunerazione sono concordati con DapNow.</p></div></>
        ) : status === "pending" ? (
          <><ShieldAlert className="h-5 w-5 text-amber-700" /><div><p className="font-semibold tracking-tight">IBAN in verifica</p><p className="text-xs text-dapnow-muted">Un admin verificherà i dati a breve.</p></div></>
        ) : (
          <><Building2 className="h-5 w-5 text-dapnow-muted" /><div><p className="font-semibold tracking-tight">Nessuna coordinata salvata</p><p className="text-xs text-dapnow-muted">Puoi lasciare vuota questa sezione finché il tuo referente non richiede i dati.</p></div></>
        )}
      </div>

      <section className="card-soft p-5 sm:p-7 space-y-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Building2 className="h-5 w-5" /></span>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold tracking-tight">Conto bancario</h2>
            <p className="text-sm text-dapnow-muted">I dati sono visibili solo a te e al team admin.</p>
          </div>
          {hasIban && !editing && (
            <button onClick={() => setEditing(true)} data-testid="payout-edit-btn" className="text-sm font-semibold text-dapnow-blue">Modifica</button>
          )}
        </div>

        {!editing && hasIban ? (
          <div className="space-y-3 text-sm">
            <Row k="IBAN" v={<span className="font-mono">{data.iban_masked}</span>} />
            <Row k="Intestatario" v={data.account_holder || "—"} />
            <Row k="Banca" v={data.bank_name || "—"} />
            <Row k="Valuta" v={data.currency} />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold tracking-tight">IBAN <span className="text-dapnow-red">*</span></label>
              <input
                data-testid="payout-iban-input"
                value={form.payout_iban}
                onChange={(e) => setForm((s) => ({ ...s, payout_iban: e.target.value.toUpperCase() }))}
                className="input-tall mt-1.5 w-full font-mono"
                placeholder="CH93 0076 2011 6238 5295 7"
              />
              <p className="text-xs text-dapnow-muted mt-1">Min. 15 caratteri, formato internazionale.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold tracking-tight">Intestatario <span className="text-dapnow-red">*</span></label>
                <input data-testid="payout-holder-input" value={form.payout_account_holder} onChange={(e) => setForm((s) => ({ ...s, payout_account_holder: e.target.value }))} className="input-tall mt-1.5 w-full" placeholder="Mario Rossi" />
              </div>
              <div>
                <label className="text-sm font-semibold tracking-tight">Banca</label>
                <input data-testid="payout-bank-input" value={form.payout_bank_name} onChange={(e) => setForm((s) => ({ ...s, payout_bank_name: e.target.value }))} className="input-tall mt-1.5 w-full" placeholder="UBS" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              {editing && hasIban && <button onClick={() => { setEditing(false); load(); }} className="btn-ghost">Annulla</button>}
              <button onClick={save} disabled={busy} data-testid="payout-save-btn" className="btn-primary">
                {busy ? "Salvataggio…" : "Salva e invia per verifica"}
              </button>
            </div>
          </div>
        )}
      </section>

      <p className="text-xs text-dapnow-muted">Salvare un IBAN non attiva pagamenti automatici. Per informazioni sulla remunerazione contatta il tuo referente DapNow.</p>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-baseline border-b border-dapnow-line pb-3 last:border-0 last:pb-0">
      <span className="overline text-[10px]">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
